import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BillingHistory } from '@/types/license';

interface BillingHistoryFilters {
  clientId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export const useBillingHistory = (filters?: BillingHistoryFilters) => {
  const {
    data: billingHistory = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['billing-history', filters],
    queryFn: async () => {
      let query = supabase
        .from('billing_history')
        .select(`
          *,
          client_license:client_licenses (
            id,
            client:clients (
              id,
              name
            ),
            license_plan:license_plans (
              id,
              name
            )
          )
        `)
        .order('invoice_date', { ascending: false });

      // Aplicar filtros
      if (filters?.clientId) {
        query = query.eq('client_license.client_id', filters.clientId);
      }

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.startDate) {
        query = query.gte('invoice_date', filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte('invoice_date', filters.endDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data as (BillingHistory & {
        client_license: {
          id: string;
          client: { id: string; name: string };
          license_plan: { id: string; name: string };
        };
      })[];
    }
  });

  const getTotalRevenue = (period: 'month' | 'year' = 'month') => {
    const now = new Date();
    const startDate = new Date(now);
    
    if (period === 'month') {
      startDate.setMonth(now.getMonth() - 1);
    } else {
      startDate.setFullYear(now.getFullYear() - 1);
    }

    return billingHistory
      .filter(bill => {
        const billDate = new Date(bill.invoice_date);
        return billDate >= startDate && bill.status === 'paid';
      })
      .reduce((sum, bill) => sum + bill.amount, 0);
  };

  const getOverdueAmount = () => {
    return billingHistory
      .filter(bill => bill.status === 'overdue')
      .reduce((sum, bill) => sum + bill.amount, 0);
  };

  const getPendingAmount = () => {
    return billingHistory
      .filter(bill => bill.status === 'pending')
      .reduce((sum, bill) => sum + bill.amount, 0);
  };

  return {
    billingHistory,
    isLoading,
    error,
    refetch,
    getTotalRevenue,
    getOverdueAmount,
    getPendingAmount
  };
};